import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  MagnifyingGlass, Hash, TrendUp, Users, Fire, Trophy, UsersThree, ArrowRight, Play,
} from "@phosphor-icons/react";
import { resolveMedia } from "@/lib/media";
import ContinueWatching from "@/components/ContinueWatching";

const CATEGORIES = [
  { name: "Comedy", color: "from-yellow-500/20 to-orange-500/20", ring: "ring-yellow-400/30" },
  { name: "Music", color: "from-pink-500/20 to-fuchsia-500/20", ring: "ring-pink-400/30" },
  { name: "Dance", color: "from-purple-500/20 to-indigo-500/20", ring: "ring-purple-400/30" },
  { name: "Sports", color: "from-emerald-500/20 to-teal-500/20", ring: "ring-emerald-400/30" },
  { name: "Food", color: "from-orange-500/20 to-red-500/20", ring: "ring-orange-400/30" },
  { name: "Beauty", color: "from-rose-500/20 to-pink-500/20", ring: "ring-rose-400/30" },
  { name: "Lifestyle", color: "from-blue-500/20 to-cyan-500/20", ring: "ring-blue-400/30" },
  { name: "Education", color: "from-indigo-500/20 to-blue-500/20", ring: "ring-indigo-400/30" },
  { name: "Gaming", color: "from-violet-500/20 to-purple-500/20", ring: "ring-violet-400/30" },
  { name: "Tech", color: "from-slate-500/20 to-zinc-500/20", ring: "ring-slate-400/30" },
];

export default function Discover() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [trendingTags, setTrendingTags] = useState([]);
  const [trendingCreators, setTrendingCreators] = useState([]);
  const [popular, setPopular] = useState([]);
  const [searching, setSearching] = useState(false);

  const loadInitial = useCallback(async () => {
    try {
      const [t, c, p] = await Promise.all([
        api.get("/hashtags/trending"),
        api.get("/creators/trending"),
        api.get("/posts/popular", { params: { limit: 12 } }),
      ]);
      setTrendingTags(t.data);
      setTrendingCreators(c.data);
      setPopular(p.data);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  useEffect(() => {
    if (!q.trim()) {
      setResults(null);
      return;
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/search", { params: { q: q.trim() } });
        setResults(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Search failed");
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [q]);

  const searchByTag = (tag) => {
    setQ("#" + tag);
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6" data-testid="discover-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-black">Discover</h1>
        <p className="text-white/60 mt-1">Trending creators, hashtags and popular content.</p>
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlass
          size={18}
          weight="bold"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50"
        />
        <Input
          data-testid="discover-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search creators, hashtags, captions…"
          className="pl-9 h-12 bg-white/5 border-white/10 text-white text-base"
        />
      </div>

      {results ? (
        <SearchResults results={results} searching={searching} onOpenTag={searchByTag} navigate={navigate} />
      ) : (
        <>
          <ContinueWatching />
          {/* Quick links row: Leaderboard + Referrals + Withdraw */}
          <div className="grid grid-cols-3 gap-3">
            <QuickLink to="/leaderboard" icon={Trophy} label="Leaders" gradient="from-yellow-500 to-orange-500" testId="quick-leaderboard" />
            <QuickLink to="/referrals" icon={UsersThree} label="Referrals" gradient="from-emerald-500 to-teal-500" testId="quick-referrals" />
            <QuickLink to="/withdraw" icon={ArrowRight} label="Withdraw" gradient="from-blue-500 to-indigo-500" testId="quick-withdraw" />
          </div>

          {/* Trending Hashtags */}
          <Section title="Trending hashtags" icon={Hash}>
            {trendingTags.length === 0 ? (
              <p className="text-white/50 text-sm">No hashtags yet — post content with #tags to start trends.</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="trending-tags">
                {trendingTags.map((t) => (
                  <button
                    key={t.tag}
                    data-testid={`tag-${t.tag}`}
                    onClick={() => searchByTag(t.tag)}
                    className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-sm transition-colors flex items-center gap-1.5"
                  >
                    <TrendUp size={14} weight="bold" className="text-emerald-400" />
                    <span className="font-heading font-bold">#{t.tag}</span>
                    <span className="text-xs text-white/50">{t.count}</span>
                  </button>
                ))}
              </div>
            )}
          </Section>

          {/* Categories */}
          <Section title="Categories" icon={Fire}>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c.name}
                  data-testid={`category-${c.name}`}
                  onClick={() => setQ(c.name)}
                  className={`aspect-[4/3] rounded-lg bg-gradient-to-br ${c.color} ring-1 ${c.ring} hover:brightness-110 transition flex items-center justify-center`}
                >
                  <span className="font-heading font-black text-lg">{c.name}</span>
                </button>
              ))}
            </div>
          </Section>

          {/* Trending Creators */}
          <Section title="Trending creators" icon={Users}>
            {trendingCreators.length === 0 ? (
              <p className="text-white/50 text-sm">Follow creators to see trending here.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="trending-creators">
                {trendingCreators.slice(0, 8).map((u) => (
                  <button
                    key={u.user_id}
                    data-testid={`creator-${u.user_id}`}
                    onClick={() => navigate(`/u/${u.user_id}`)}
                    className="flex flex-col items-center p-4 rounded-lg bg-white/[0.03] border border-white/10 hover:bg-white/[0.06] transition"
                  >
                    {u.picture ? (
                      <img src={u.picture} alt="" className="w-16 h-16 rounded-full ring-2 ring-white/10" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-white/10" />
                    )}
                    <p className="mt-2 font-heading font-bold text-sm truncate max-w-full">{u.name}</p>
                    <p className="text-xs text-white/50">{u.followers} followers</p>
                  </button>
                ))}
              </div>
            )}
          </Section>

          {/* Popular */}
          <Section title="Popular now" icon={Fire}>
            {popular.length === 0 ? (
              <p className="text-white/50 text-sm">No content yet.</p>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-1.5" data-testid="popular-grid">
                {popular.map((p) => (
                  <button
                    key={p.post_id}
                    data-testid={`popular-${p.post_id}`}
                    onClick={() => navigate(p.content_type === "video" ? `/w/${p.post_id}` : "/feed")}
                    className="relative aspect-video bg-black rounded-md overflow-hidden group"
                  >
                    {p.content_type === "video" && (p.thumbnail_url || p.media_url) && (
                      <img src={resolveMedia(p.thumbnail_url || p.media_url)} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    )}
                    {p.content_type === "image" && p.media_url && (
                      <img src={resolveMedia(p.media_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    )}
                    {p.content_type === "text" && (
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-black to-emerald-900 flex items-center justify-center p-2">
                        <p className="text-[10px] font-heading font-bold text-white text-center line-clamp-5">{p.caption}</p>
                      </div>
                    )}
                    <div className="absolute bottom-1 left-1 right-1 flex items-center gap-1 text-[10px] text-white font-bold">
                      <Play size={10} weight="fill" /> {p.view_count}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Section>
        </>
      )}
    </div>
  );
}

function QuickLink({ to, icon: Icon, label, gradient, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`p-4 rounded-xl bg-gradient-to-br ${gradient} text-white flex flex-col items-center gap-2 hover:brightness-110 transition-all active:scale-95`}
    >
      <Icon size={22} weight="fill" />
      <span className="font-heading font-bold text-sm">{label}</span>
    </Link>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Icon size={18} weight="bold" className="text-white/70" />
        <h2 className="font-heading text-xl font-black">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function SearchResults({ results, searching, onOpenTag, navigate }) {
  if (searching) return <p className="text-white/60 text-sm">Searching…</p>;
  const { users, posts, hashtags } = results;
  const empty = users.length === 0 && posts.length === 0 && hashtags.length === 0;
  if (empty) return <p className="text-white/50 text-sm py-6 text-center">No results.</p>;
  return (
    <div className="space-y-6">
      {hashtags.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-widest font-bold text-white/50 mb-2">Hashtags</p>
          <div className="flex flex-wrap gap-2" data-testid="search-hashtags">
            {hashtags.map((t) => (
              <button
                key={t.tag}
                onClick={() => onOpenTag(t.tag)}
                className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-sm"
              >
                #{t.tag} <span className="text-xs text-white/50">{t.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {users.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-widest font-bold text-white/50 mb-2">People</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="search-users">
            {users.map((u) => (
              <button
                key={u.user_id}
                onClick={() => navigate(`/u/${u.user_id}`)}
                className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/10 hover:bg-white/[0.06] transition text-left"
              >
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-white/10" />
                )}
                <p className="font-heading font-bold truncate">{u.name}</p>
              </button>
            ))}
          </div>
        </div>
      )}
      {posts.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-widest font-bold text-white/50 mb-2">Posts</p>
          <Card className="bg-[#121212] border-white/10">
            <CardContent className="p-2">
              <div className="grid grid-cols-3 gap-1.5" data-testid="search-posts">
                {posts.map((p) => (
                  <button
                    key={p.post_id}
                    onClick={() => navigate("/feed")}
                    className="relative aspect-[9/16] bg-black rounded-md overflow-hidden"
                  >
                    {p.content_type === "video" && (p.thumbnail_url || p.media_url) && (
                      <img src={resolveMedia(p.thumbnail_url || p.media_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    )}
                    {p.content_type === "image" && p.media_url && (
                      <img src={resolveMedia(p.media_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    )}
                    {p.content_type === "text" && (
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-black to-emerald-900 flex items-center justify-center p-2">
                        <p className="text-[10px] font-heading font-bold text-white text-center line-clamp-4">{p.caption}</p>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
